import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

import {
    AssistantPocFeedbackChip,
    LESSON_WORTHY_CHIPS,
} from '../../../services/ai-poc/ai-poc.service';
import { SimpleBadgeComponent } from '../../v2-layout/shared/simple-badge/simple-badge.component';

interface ChipOption {
    id: AssistantPocFeedbackChip;
    label: string;
    /** Placeholder used when the user opens the Send-feedback panel for this chip. */
    detailPlaceholder: string;
}

/**
 * Inline reason-chips panel that animates in under an assistant message
 * when the user clicks thumbs-down. Per the Teach Gable design spec:
 *
 *   thumbs-down → chips → (super + lesson-chip) Teach/Flag/Skip CTAs
 *                       → (otherwise)           Send-feedback panel
 *
 * Owns no async state of its own — the parent (gable-drawer-host) handles
 * persisting the chip telemetry, opening the Teach drawer, and dismissal.
 * This component is purely the visual + interaction layer.
 *
 * Animation: opacity + max-height transition managed via the `is-open`
 * class. Width/padding stay constant so we don't reflow neighboring
 * bubbles when we open or close.
 */
@Component({
    selector: 'app-gable-feedback-panel',
    templateUrl: './gable-feedback-panel.component.html',
    styleUrls: ['./gable-feedback-panel.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        SimpleBadgeComponent,
    ],
})
export class GableFeedbackPanelComponent {
    /** True when thumbs-down is selected for this message. Drives the slide-in animation. */
    @Input() open = false;
    /** Whether the current user is a super admin — controls the Teach CTA visibility. */
    @Input() isSuperUser = false;

    @Output() readonly chipSelected = new EventEmitter<{ chip: AssistantPocFeedbackChip; detail?: string }>();
    /** Super user picked a lesson-worthy chip and clicked "Teach Gable". */
    @Output() readonly teachRequested = new EventEmitter<{ chip: AssistantPocFeedbackChip }>();
    /**
     * Super user picked a lesson-worthy chip and clicked "Flag for team to
     * review" — telemetry-only path. Stores the chip + Gable's answer for
     * the team, NO lesson is created.
     */
    @Output() readonly flagRequested = new EventEmitter<{ chip: AssistantPocFeedbackChip }>();
    /** User dismissed the panel via Skip. */
    @Output() readonly skipped = new EventEmitter<void>();

    readonly chips: ReadonlyArray<ChipOption> = [
        { id: 'wrong_data', label: 'Wrong data', detailPlaceholder: 'What number or fact was off?' },
        { id: 'missed_nuance', label: 'Missed nuance', detailPlaceholder: 'What did Gable miss?' },
        { id: 'wrong_tone', label: 'Wrong tone', detailPlaceholder: 'What felt off about the phrasing or tone?' },
        { id: 'off_topic', label: 'Off-topic', detailPlaceholder: 'What did you expect Gable to answer instead?' },
        { id: 'other', label: 'Other', detailPlaceholder: 'Tell us what was wrong…' },
    ];

    selectedChip: AssistantPocFeedbackChip | null = null;
    detailText = '';

    constructor(private readonly _cdr: ChangeDetectorRef) {}

    get isLessonWorthyChip(): boolean {
        return this.selectedChip !== null && LESSON_WORTHY_CHIPS.includes(this.selectedChip);
    }

    get showTeachCta(): boolean {
        return this.isLessonWorthyChip && this.isSuperUser;
    }

    get showSendPanel(): boolean {
        // Lesson-worthy chip for non-supers also lands here — they get the
        // Send-feedback path since they can't author lessons.
        return this.selectedChip !== null && !this.showTeachCta;
    }

    get currentDetailPlaceholder(): string {
        return this.chips.find((c) => c.id === this.selectedChip)?.detailPlaceholder ?? '';
    }

    onChipClick(chip: AssistantPocFeedbackChip): void {
        // Re-clicking the selected chip clears it (single-select toggle).
        if (this.selectedChip === chip) {
            this.selectedChip = null;
            this.detailText = '';
        } else {
            this.selectedChip = chip;
            this.detailText = '';
        }
        this._cdr.markForCheck();
    }

    onTeachClick(): void {
        if (!this.selectedChip || !this.isLessonWorthyChip) { return; }
        this.teachRequested.emit({ chip: this.selectedChip });
    }

    onFlagClick(): void {
        if (!this.selectedChip) { return; }
        this.flagRequested.emit({ chip: this.selectedChip });
    }

    onSendClick(): void {
        if (!this.selectedChip) { return; }
        this.chipSelected.emit({
            chip: this.selectedChip,
            detail: this.detailText.trim() === '' ? undefined : this.detailText.trim(),
        });
    }

    /**
     * Skip still records the chip selection (the chip alone is useful
     * telemetry) but without the free-text detail. The host marks the
     * interaction as feedback-sent and swaps the panel for the green
     * confirmation, same as Send feedback does.
     */
    onSkipClick(): void {
        if (this.selectedChip) {
            this.chipSelected.emit({ chip: this.selectedChip });
        }
        this.skipped.emit();
    }

    /** Reset internal state when the panel closes (e.g. parent collapsed it). */
    reset(): void {
        this.selectedChip = null;
        this.detailText = '';
        this._cdr.markForCheck();
    }
}
